import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Package, ShoppingCart, DollarSign, TrendingUp, 
  Truck, Eye, Trash2, LogOut, RefreshCw, Search,
  ChevronDown, ChevronUp, Calendar, AlertCircle, Mail, BarChart3,
  Zap, Store, Send, Bell, Settings, Megaphone, Star, Banknote, CheckCircle, XCircle, Building2, Briefcase, Flag, Ban, MessageSquare, Bot, LayoutDashboard, LifeBuoy, ShieldCheck, KeyRound
} from 'lucide-react';
import { AITeamTab } from '../components/admin/AITeamTab';
import { QueriesTab } from '../components/admin/QueriesTab';
import { PrivateCollectionAdmin } from '../components/admin/PrivateCollectionAdmin';
import { RevenueLedger } from '../components/admin/RevenueLedger';
import { ShippingMarginReport } from '../components/admin/ShippingMarginReport';
import { ActionRequired } from '../components/admin/ActionRequired';
import { FoundingTenTracker } from '../components/admin/FoundingTenTracker';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { GrowthChart, DailyActivityChart, SalesChart, CategoryChart, UserActivityChart, TopProductsChart } from '../components/admin/AdminCharts';
import { useApp } from '../context/AppContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MessageLogTab = () => {
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const loadMessages = async (flagged = flaggedOnly, q = search) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ flagged: flagged ? 'true' : 'false', limit: '100' });
      if (q) params.set('search', q);
      const res = await fetch(`${BACKEND_URL}/api/admin/messages?${params}`);
      const data = await res.json();
      setMessages(data.messages || []);
      setTotal(data.total || 0);
    } catch {
      toast.error('Could not load messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadMessages(); }, []);

  const flagBadge = (m) => {
    const label = m.ai_verdict?.label;
    if (label && !['ok', 'unchecked'].includes(label)) {
      const colors = { scam: 'bg-red-100 text-red-700', abusive: 'bg-red-100 text-red-700', off_platform: 'bg-orange-100 text-orange-700', spam: 'bg-amber-100 text-amber-700' };
      return <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full ${colors[label]}`} title={m.ai_verdict?.reason}>🤖 {label.replace('_', ' ')}</span>;
    }
    if (m.is_suspicious) return <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-600">suspicious</span>;
    if (label === 'ok') return <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">✓ ok</span>;
    return null;
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="admin-messages-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-emerald-700" />
          Message Log <span className="text-sm text-gray-400 font-normal">({total})</span>
        </h3>
        <div className="flex items-center gap-2">
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && loadMessages(flaggedOnly, search)}
            placeholder="Search name or text..."
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm w-44"
            data-testid="message-log-search"
          />
          <Button
            size="sm"
            variant={flaggedOnly ? 'default' : 'outline'}
            className={flaggedOnly ? 'bg-red-600 hover:bg-red-700' : ''}
            onClick={() => { const f = !flaggedOnly; setFlaggedOnly(f); loadMessages(f, search); }}
            data-testid="flagged-only-toggle"
          >
            <Flag className="w-3.5 h-3.5 mr-1" /> {flaggedOnly ? 'Flagged Only' : 'All Messages'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => loadMessages()} data-testid="refresh-messages-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>
      {loading ? (
        <p className="text-gray-500 text-sm py-8 text-center">Loading messages...</p>
      ) : messages.length === 0 ? (
        <p className="text-gray-500 text-sm py-8 text-center">No messages found.</p>
      ) : (
        <div className="space-y-2 max-h-[70vh] overflow-y-auto">
          {messages.map(m => (
            <div key={m.id} className={`border rounded-lg p-3 ${(m.is_suspicious || ['scam','spam','off_platform','abusive'].includes(m.ai_verdict?.label)) ? 'border-red-200 bg-red-50/40' : 'border-gray-100'}`} data-testid={`message-log-row-${m.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                <p className="text-xs text-gray-700">
                  <strong>{m.sender_name}</strong> <span className="text-gray-400">→</span> <strong>{m.receiver_name}</strong>
                </p>
                <div className="flex items-center gap-2">
                  {flagBadge(m)}
                  <span className="text-[10px] text-gray-400">{m.timestamp ? new Date(m.timestamp).toLocaleString() : ''}</span>
                </div>
              </div>
              <p className="text-sm text-gray-900 break-words">{m.text}</p>
              {m.ai_verdict?.reason && !['ok', 'unchecked'].includes(m.ai_verdict?.label) && (
                <p className="text-[11px] text-red-600 mt-1 italic">AI: {m.ai_verdict.reason}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PushSubscriberBadge = () => {
  const [stats, setStats] = useState(null);

  const load = () => {
    fetch(`${BACKEND_URL}/api/admin/push-stats`)
      .then(r => r.json())
      .then(setStats)
      .catch(() => {});
  };

  useEffect(() => { load(); }, []);

  if (!stats) return <span className="text-xs text-gray-400">Loading reach...</span>;
  return (
    <div className="flex items-center gap-2" data-testid="push-subscriber-badge">
      <span className="text-xs bg-emerald-50 text-emerald-700 font-semibold px-2.5 py-1 rounded-full">
        📱 {stats.messageable} reachable device{stats.messageable === 1 ? '' : 's'}
      </span>
      <span className="text-[11px] text-gray-400">{stats.players} total registered</span>
      <button onClick={load} className="text-gray-400 hover:text-gray-600" title="Refresh" data-testid="refresh-push-stats">
        <RefreshCw className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

const ReportsTab = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const loadReports = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/reports?status=open`);
      const data = await res.json();
      setReports(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Could not load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadReports(); }, []);

  const resolveReport = async (reportId) => {
    setActing(reportId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/reports/${reportId}/resolve`, { method: 'PUT' });
      if (!res.ok) throw new Error();
      setReports(prev => prev.filter(r => r.id !== reportId));
      toast.success('Report resolved');
    } catch {
      toast.error('Could not resolve report');
    } finally {
      setActing(null);
    }
  };

  const banUser = async (report) => {
    if (!window.confirm(`Ban ${report.reported_name}? They won't be able to log in or message anyone.`)) return;
    setActing(report.id);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${report.reported_user_id}/ban`, { method: 'POST' });
      if (!res.ok) throw new Error();
      await fetch(`${BACKEND_URL}/api/admin/reports/${report.id}/resolve`, { method: 'PUT' });
      setReports(prev => prev.filter(r => r.id !== report.id));
      toast.success(`${report.reported_name} banned and report resolved`);
    } catch {
      toast.error('Could not ban user');
    } finally {
      setActing(null);
    }
  };

  const reasonColors = {
    scam: 'bg-red-100 text-red-700',
    harassment: 'bg-orange-100 text-orange-700',
    spam: 'bg-amber-100 text-amber-700',
    other: 'bg-gray-100 text-gray-600'
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="admin-reports-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <Flag className="w-4 h-4 text-red-500" />
          Open User Reports ({reports.length})
        </h3>
        <Button variant="outline" size="sm" onClick={loadReports} data-testid="refresh-reports-btn">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>
      {loading ? (
        <p className="text-gray-500 text-sm py-8 text-center">Loading reports...</p>
      ) : reports.length === 0 ? (
        <div className="text-center py-10">
          <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
          <p className="text-gray-600 font-medium">No open reports</p>
          <p className="text-gray-400 text-sm">All clear — nothing needs your attention.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map(report => (
            <div key={report.id} className="border border-gray-100 rounded-xl p-4" data-testid={`report-row-${report.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full ${reasonColors[report.reason] || reasonColors.other}`}>
                      {report.reason}
                    </span>
                    <span className="text-xs text-gray-400">{new Date(report.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-sm text-gray-900">
                    <strong>{report.reporter_name}</strong> reported <strong>{report.reported_name}</strong>
                  </p>
                  {report.details && <p className="text-xs text-gray-500 mt-1 italic">"{report.details}"</p>}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button
                    size="sm" variant="outline"
                    onClick={() => resolveReport(report.id)}
                    disabled={acting === report.id}
                    data-testid={`resolve-report-${report.id}`}
                  >
                    <CheckCircle className="w-4 h-4 mr-1 text-emerald-600" /> Resolve
                  </Button>
                  <Button
                    size="sm"
                    className="bg-red-600 hover:bg-red-700"
                    onClick={() => banUser(report)}
                    disabled={acting === report.id}
                    data-testid={`ban-user-${report.id}`}
                  >
                    <Ban className="w-4 h-4 mr-1" /> Ban User
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CardFeeSettings = () => {
  const [fee, setFee] = useState('');
  const [saving, setSaving] = useState(false);
  const [currentFee, setCurrentFee] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/settings/card-fee`)
      .then(r => r.json())
      .then(d => { setCurrentFee(d.amount); setFee(d.amount != null ? d.amount.toFixed(2) : ''); })
      .catch(() => {});
  }, []);

  const saveFee = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/settings/card-fee`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: parseFloat(fee) })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setCurrentFee(data.amount);
        toast.success(`Card Processing Fee updated to $${data.amount.toFixed(2)}`);
      } else {
        throw new Error(data.detail || 'Update failed');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to update fee');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="card-fee-settings">
      <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
        💳 Card Processing Fee
      </h3>
      <p className="text-xs text-gray-500 mb-4">
        Flat fee added to every online (Stripe) purchase to cover card costs. Cash sales are unaffected.
        {currentFee !== null && <span className="font-medium text-gray-700"> Current: ${currentFee.toFixed(2)} AUD</span>}
      </p>
      <div className="flex items-center gap-2 max-w-xs">
        <span className="text-gray-500 font-medium">$</span>
        <Input
          type="number" min="0" max="50" step="0.10"
          value={fee}
          onChange={e => setFee(e.target.value)}
          className="h-10"
          data-testid="card-fee-input"
        />
        <Button onClick={saveFee} disabled={saving || fee === ''} size="sm" className="h-10" data-testid="card-fee-save-btn">
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

const NAV_GROUPS = [
  { label: 'Command', items: [
    { id: 'overview', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'aiteam', label: 'AI Team', icon: Bot },
    { id: 'queries', label: 'Queries', icon: LifeBuoy },
  ]},
  { label: 'Operations', items: [
    { id: 'actions', label: 'Actions', icon: Zap },
    { id: 'orders', label: 'Orders', icon: ShoppingCart },
    { id: 'finance', label: 'Finance & Payouts', icon: DollarSign },
    { id: 'collection', label: 'Private Collection', icon: KeyRound },
  ]},
  { label: 'Community', items: [
    { id: 'users', label: 'Users', icon: Users },
    { id: 'products', label: 'Listings', icon: Package },
    { id: 'services', label: 'Services', icon: Briefcase },
    { id: 'reports', label: 'Moderation', icon: ShieldCheck },
  ]},
];

const NAV_FLAT = NAV_GROUPS.flatMap(g => g.items);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { refreshProducts } = useApp();
  const adminUser = JSON.parse(localStorage.getItem('minted_admin') || 'null');
  const [testInbox, setTestInbox] = useState(adminUser?.email || '');
  const [compose, setCompose] = useState({ to: '', subject: '', message: '' });
  const [composeSending, setComposeSending] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sendingEmails, setSendingEmails] = useState(false);
  const [pendingPromotions, setPendingPromotions] = useState([]);
  const [showStoreModal, setShowStoreModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [storeForm, setStoreForm] = useState({ store_name: '', store_description: '' });
  const [actionLoading, setActionLoading] = useState({});
  const [adminServices, setAdminServices] = useState([]);
  
  // Finance state
  const [financeData, setFinanceData] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [financePeriod, setFinancePeriod] = useState('month');
  const [financeLoading, setFinanceLoading] = useState(false);
  
  // Email state
  const [testEmailAddr, setTestEmailAddr] = useState('');
  const [creditEmail, setCreditEmail] = useState('');
  const [creditAmount, setCreditAmount] = useState('');
  const [adjustEmail, setAdjustEmail] = useState('');
  const [adjustAvailable, setAdjustAvailable] = useState('');
  const [adjustPending, setAdjustPending] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [emailLogs, setEmailLogs] = useState([]);
  
  // Login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Product search state
  const [productSearch, setProductSearch] = useState({ email: '', title: '', category: '' });
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  const [pushTitle, setPushTitle] = useState('');
  const [pushMessage, setPushMessage] = useState('');
  
  // Payout requests state
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [queriesSummary, setQueriesSummary] = useState({});

  // Check if admin is logged in (validates admin_token cookie with backend)
  useEffect(() => {
    document.body.classList.add('admin-fullwidth');
    return () => document.body.classList.remove('admin-fullwidth');
  }, []);

  useEffect(() => {
    const adminToken = localStorage.getItem('minted_admin');
    if (adminToken) {
      fetch(`${BACKEND_URL}/api/admin/auth/check`)
        .then(r => {
          if (r.ok) {
            setIsAuthenticated(true);
            fetchDashboardData();
          } else {
            localStorage.removeItem('minted_admin');
          }
        })
        .catch(() => {});
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        toast.error(errData.detail || `Login failed (${response.status})`);
        setLoginLoading(false);
        return;
      }
      
      const data = await response.json();
      
      if (data.success) {
        localStorage.setItem('minted_admin', JSON.stringify(data.user));
        setIsAuthenticated(true);
        toast.success('Welcome to Admin Dashboard');
        fetchDashboardData();
      } else {
        toast.error(data.detail || 'Invalid credentials');
      }
    } catch (error) {
      console.error('Admin login error:', error);
      toast.error(`Login error: ${error.message || 'Network error — check your connection'}`);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    fetch(`${BACKEND_URL}/api/admin/logout`, { method: 'POST' }).catch(() => {});
    localStorage.removeItem('minted_admin');
    setIsAuthenticated(false);
    toast.success('Logged out');
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const dashResponse = await fetch(`${BACKEND_URL}/api/admin/dashboard`);
      if (dashResponse.ok) {
        const data = await dashResponse.json();
        setDashboardData(data);
      }
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    }
    // Fetch analytics separately - don't let it block the dashboard
    try {
      const analyticsResponse = await fetch(`${BACKEND_URL}/api/admin/analytics`);
      if (analyticsResponse.ok) {
        const analytics = await analyticsResponse.json();
        setAnalyticsData(analytics);
      }
    } catch (error) {
      console.error('Analytics fetch error:', error);
    }
    try {
      const qRes = await fetch(`${BACKEND_URL}/api/admin/queries/summary`);
      if (qRes.ok) setQueriesSummary(await qRes.json());
    } catch {}
    setLoading(false);
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users`);
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/products`);
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      toast.error('Failed to load products');
    }
  };

  const [paymentTransactions, setPaymentTransactions] = useState([]);
  const [reconciling, setReconciling] = useState(false);

  const fetchOrders = async () => {
    try {
      const [ordersRes, txnsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/orders`),
        fetch(`${BACKEND_URL}/api/admin/payment-transactions`)
      ]);
      const ordersData = await ordersRes.json();
      setOrders(ordersData);
      try {
        const txnsData = await txnsRes.json();
        setPaymentTransactions(txnsData);
      } catch {}
    } catch (error) {
      toast.error('Failed to load orders');
    }
  };

  const handleReconcile = async () => {
    setReconciling(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/reconcile-payments`, { method: 'POST' });
      const data = await res.json();
      toast.success(`Checked ${data.pending_checked} pending payments`);
      fetchOrders();
    } catch {
      toast.error('Reconciliation failed');
    } finally {
      setReconciling(false);
    }
  };

  const fetchPayoutRequests = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/payout-requests`);
      if (res.ok) {
        const data = await res.json();
        setPayoutRequests(data);
      }
    } catch (error) {
      console.error('Failed to load payout requests:', error);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'overview') fetchDashboardData();
    if (tab === 'users' || tab === 'actions') fetchUsers();
    if (tab === 'products') fetchProducts();
    if (tab === 'orders') fetchOrders();
    if (tab === 'actions') { fetchPendingPromotions(); fetchEmailLogs(); }
    if (tab === 'finance') { fetchFinanceData(financePeriod); fetchPayoutRequests(); }
    if (tab === 'services') fetchAdminServices();
  };

  const fetchAdminServices = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/services`);
      if (res.ok) {
        const data = await res.json();
        setAdminServices(data);
      }
    } catch (err) {
      console.error('Failed to fetch services:', err);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/analytics`);
      const data = await response.json();
      setAnalyticsData(data);
    } catch (error) {
      toast.error('Failed to load analytics');
    }
  };

  const fetchPendingPromotions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/pending-promotions`);
      const data = await response.json();
      setPendingPromotions(data);
    } catch (error) {
      console.error('Failed to load pending promotions');
    }
  };

  // Finance data fetching
  const fetchFinanceData = async (period = 'month') => {
    setFinanceLoading(true);
    try {
      const [summaryRes, payoutsRes, disputesRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/finance/summary?period=${period}`),
        fetch(`${BACKEND_URL}/api/admin/finance/payouts`),
        fetch(`${BACKEND_URL}/api/admin/finance/disputes`)
      ]);
      
      const summary = await summaryRes.json();
      const payoutsData = await payoutsRes.json();
      const disputesData = await disputesRes.json();
      
      setFinanceData(summary);
      setPayouts(payoutsData);
      setDisputes(disputesData);
    } catch (error) {
      console.error('Failed to load finance data:', error);
      toast.error('Failed to load finance data');
    } finally {
      setFinanceLoading(false);
    }
  };

  const processPayout = async (payoutId, action) => {
    setActionLoading(prev => ({ ...prev, [payoutId]: true }));
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/finance/payouts/${payoutId}/process?action=${action}`, {
        method: 'PUT'
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Payout ${action === 'complete' ? 'completed' : 'marked as failed'}`);
        fetchFinanceData(financePeriod);
      }
    } catch (error) {
      toast.error('Failed to process payout');
    } finally {
      setActionLoading(prev => ({ ...prev, [payoutId]: false }));
    }
  };

  const resolveDispute = async (disputeId, winner) => {
    const resolution = prompt('Enter resolution notes:');
    if (!resolution) return;
    
    setActionLoading(prev => ({ ...prev, [disputeId]: true }));
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/disputes/${disputeId}/resolve?resolution=${encodeURIComponent(resolution)}&winner=${winner}`, {
        method: 'PUT'
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Dispute resolved in favor of ${winner}`);
        fetchFinanceData(financePeriod);
      }
    } catch (error) {
      toast.error('Failed to resolve dispute');
    } finally {
      setActionLoading(prev => ({ ...prev, [disputeId]: false }));
    }
  };

  const exportFinanceData = async (reportType = 'all') => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/finance/export?report_type=${reportType}`);
      const data = await response.json();
      
      // Download as JSON
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `minted-finance-${reportType}-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success('Finance data exported');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const releasePendingFunds = async () => {
    if (!window.confirm('Release all funds that have passed the 7-day hold period?')) return;
    setActionLoading(prev => ({ ...prev, releaseFunds: true }));
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/release-pending-funds`, { method: 'POST' });
      const data = await response.json();
      toast.success(`Released funds for ${data.released_count} orders`);
      fetchFinanceData(financePeriod);
    } catch (error) {
      toast.error('Failed to release funds');
    } finally {
      setActionLoading(prev => ({ ...prev, releaseFunds: false }));
    }
  };

  const sendReengagementEmails = async () => {
    if (!window.confirm('Send re-engagement emails to inactive users?')) return;
    setSendingEmails(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/send-reengagement`, { method: 'POST' });
      const data = await response.json();
      toast.success(`Campaign started — sending to ${data.recipients} inactive users in the background. You'll get a notification when it finishes.`);
    } catch (error) {
      toast.error('Failed to send emails');
    } finally {
      setSendingEmails(false);
    }
  };

  const sendWeeklyNewsletter = async () => {
    if (!window.confirm('Send weekly newsletter to all users?')) return;
    setSendingEmails(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/send-weekly-newsletter`, { method: 'POST' });
      await response.json();
      toast.success('Newsletter started — sending in the background. You\'ll get a notification when it finishes.');
    } catch (error) {
      toast.error('Failed to send newsletter');
    } finally {
      setSendingEmails(false);
    }
  };

  const sendPromoEmail = async () => {
    if (!window.confirm('Send promotional email to ALL users encouraging them to list items? This will send to all registered users.')) return;
    setActionLoading(prev => ({ ...prev, promo: true }));
    try {
      const response = await fetch(`${BACKEND_URL}/api/promo/list-items/send-all?secret_key=minted-promo-2025`, { method: 'POST' });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        toast.error(`Failed: ${errData.detail || response.statusText}`);
        return;
      }
      const data = await response.json();
      if (data.started) {
        toast.success(`Promo campaign started — sending to ~${data.recipients} users in the background. You'll get a notification when it finishes.`);
      } else {
        toast.warning('No users to send to');
      }
      fetchEmailLogs();
    } catch (error) {
      console.error('Promo email error:', error);
      toast.error('Failed to send promo emails — check console for details');
    } finally {
      setActionLoading(prev => ({ ...prev, promo: false }));
    }
  };

  const sendTestEmail = async () => {
    setActionLoading(prev => ({ ...prev, testEmail: true }));
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/send-test-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: testEmailAddr })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Test email sent to ${testEmailAddr}`);
        setTestEmailAddr('');
        fetchEmailLogs();
      } else {
        toast.error(data.error || 'Failed to send test email');
      }
    } catch (error) {
      toast.error('Failed to send test email');
    } finally {
      setActionLoading(prev => ({ ...prev, testEmail: false }));
    }
  };

  const fetchEmailLogs = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/email-logs`);
      const data = await response.json();
      setEmailLogs(data);
    } catch (error) {
      console.error('Email logs fetch error:', error);
    }
  };

  const activatePromotion = async (promotionId, force = false) => {
    setActionLoading(prev => ({ ...prev, [promotionId]: true }));
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/promotions/activate/${promotionId}${force ? '?force=true' : ''}`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || 'Promotion activated!');
        fetchPendingPromotions();
      } else if (response.status === 402) {
        const gift = window.confirm(`${data.detail}\n\nGift this promotion for free anyway?`);
        if (gift) return activatePromotion(promotionId, true);
      } else {
        toast.error(data.detail || 'Failed to activate promotion');
      }
    } catch (error) {
      toast.error('Failed to activate promotion');
    } finally {
      setActionLoading(prev => ({ ...prev, [promotionId]: false }));
    }
  };

  const convertUserToStore = async () => {
    if (!selectedUser) return;
    setActionLoading(prev => ({ ...prev, store: true }));
    try {
      const response = await fetch(`${BACKEND_URL}/api/users/${selectedUser.id}/convert-to-store`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          store_name: storeForm.store_name || selectedUser.name,
          store_description: storeForm.store_description
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`${selectedUser.name} is now a store!`);
        setShowStoreModal(false);
        setSelectedUser(null);
        setStoreForm({ store_name: '', store_description: '' });
        fetchUsers();
      } else {
        toast.error('Failed to convert to store');
      }
    } catch (error) {
      toast.error('Failed to convert to store');
    } finally {
      setActionLoading(prev => ({ ...prev, store: false }));
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${orderId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        toast.success('Order status updated');
        fetchOrders();
      }
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/admin/users/${userId}`, { method: 'DELETE' });
      toast.success('User deleted');
      fetchUsers();
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const renameUser = async (u) => {
    const newName = window.prompt(`Rename "${u.name}" to:`, u.name || '');
    if (!newName || newName.trim() === '' || newName.trim() === u.name) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${u.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim() })
      });
      if (!res.ok) throw new Error();
      toast.success(`Renamed to ${newName.trim()}`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to rename user');
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/admin/products/${productId}`, { method: 'DELETE' });
      toast.success('Product deleted');
      fetchProducts();
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <Toaster position="top-center" />
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-minted-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Minted Admin</h1>
            <p className="text-gray-500 mt-2">Sign in to access dashboard</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@mintedbuysell.com.au"
                required
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="admin-password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-minted-600 hover:bg-minted-700"
              disabled={loginLoading}
              data-testid="admin-login-submit"
            >
              {loginLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {};
  const activeNavItem = NAV_FLAT.find(n => n.id === activeTab) || NAV_FLAT[0];
  const openQueries = queriesSummary.open || 0;

  const NavItems = ({ mobile }) => (
    NAV_GROUPS.map((group) => (
      <div key={group.label} className={mobile ? 'flex gap-1' : ''}>
        {!mobile && (
          <p className="px-3 mb-1.5 text-[10px] uppercase tracking-[0.18em]" style={{ color: 'rgba(201,169,98,0.7)' }}>{group.label}</p>
        )}
        <div className={mobile ? 'flex gap-1' : 'space-y-0.5'}>
          {group.items.map((tab) => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                data-testid={tab.id === 'aiteam' ? 'ai-team-tab' : `admin-tab-${tab.id}`}
                className={mobile
                  ? `flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${active ? 'text-[#0f2419]' : 'text-white/70'}`
                  : `w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all ${active ? 'text-white font-medium' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
                style={active ? (mobile
                  ? { backgroundColor: '#c9a962' }
                  : { backgroundColor: 'rgba(201,169,98,0.14)', boxShadow: 'inset 2px 0 0 #c9a962' }) : {}}
              >
                <tab.icon className="w-4 h-4 shrink-0" style={active && !mobile ? { color: '#c9a962' } : {}} />
                {tab.label}
                {tab.id === 'queries' && openQueries > 0 && (
                  <span className={`ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full ${mobile ? 'bg-[#0f2419] text-white' : ''}`}
                    style={!mobile ? { backgroundColor: '#c9a962', color: '#0f2419' } : {}} data-testid="queries-nav-badge">
                    {openQueries}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    ))
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f2efe8' }}>
      <Toaster position="top-center" />

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col fixed inset-y-0 left-0 w-60 z-40 text-white" style={{ background: 'linear-gradient(180deg, #0d2117 0%, #16321f 100%)' }} data-testid="admin-sidebar">
        <div className="px-5 pt-6 pb-5" style={{ borderBottom: '1px solid rgba(201,169,98,0.18)' }}>
          <p className="text-xl tracking-[0.25em] font-semibold" style={{ fontFamily: 'Georgia, serif', color: '#c9a962' }}>MINTED</p>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mt-1">Command Centre</p>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-6">
          <NavItems />
        </nav>
        <div className="px-3 py-4 space-y-1" style={{ borderTop: '1px solid rgba(201,169,98,0.18)' }}>
          <button onClick={fetchDashboardData} disabled={loading} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-white/60 hover:text-white hover:bg-white/5 transition-colors">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh data
          </button>
          <button onClick={handleLogout} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-white/60 hover:text-white hover:bg-white/5 transition-colors" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 text-white shadow-md" style={{ backgroundColor: '#0d2117' }}>
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <div>
            <p className="text-base tracking-[0.2em] font-semibold" style={{ fontFamily: 'Georgia, serif', color: '#c9a962' }}>MINTED</p>
            <p className="text-[9px] uppercase tracking-[0.25em] text-white/40">Command Centre</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchDashboardData} disabled={loading} className="p-2 rounded-lg text-white/70 hover:bg-white/10">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={handleLogout} className="p-2 rounded-lg text-white/70 hover:bg-white/10">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="flex gap-2 overflow-x-auto px-3 pb-3">
          <NavItems mobile />
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-60">
        <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="hidden lg:flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl text-gray-900" style={{ fontFamily: 'Georgia, serif' }}>{activeNavItem.label}</h1>
            <p className="text-xs text-gray-400 mt-0.5">
              {activeTab === 'overview' ? "Live pulse of the marketplace — current numbers and where they're heading"
                : activeTab === 'queries' ? 'Customer questions, handled by your AI Support agent'
                : activeTab === 'aiteam' ? 'Your autonomous team — financial calls still come to you'
                : activeTab === 'reports' ? 'Reports, flagged messages and community safety'
                : activeTab === 'collection' ? 'The 18+ boutique — subscribers, pieces and placement revenue'
                : 'Minted admin'}
            </p>
          </div>
          <span className="text-[11px] px-3 py-1.5 rounded-full bg-white border text-gray-500">{new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
        </div>

        {/* Reports Tab */}
        {activeTab === 'reports' && <ReportsTab />}

        {/* AI Team Tab */}
        {activeTab === 'aiteam' && <AITeamTab />}

        {/* Queries Inbox */}
        {activeTab === 'queries' && <QueriesTab />}

        {/* The Private Collection (18+) */}
        {activeTab === 'collection' && <PrivateCollectionAdmin />}

        {/* Message Log (part of Moderation) */}
        {activeTab === 'reports' && <div className="mt-6"><MessageLogTab /></div>}

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Command hero */}
            <div className="rounded-2xl p-6 text-white relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0d2117 0%, #1a3a2a 60%, #234832 100%)' }} data-testid="dashboard-hero">
              <div className="absolute -top-10 -right-10 w-72 h-72 rounded-full opacity-[0.12] pointer-events-none" style={{ background: 'radial-gradient(circle, #c9a962 0%, transparent 70%)' }} />
              <p className="text-[10px] uppercase tracking-[0.25em]" style={{ color: '#c9a962' }}>The State of Minted</p>
              <h2 className="text-xl md:text-2xl mt-1" style={{ fontFamily: 'Georgia, serif' }}>
                Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'} — here's where things stand
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
                <div className="rounded-xl p-4" style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(201,169,98,0.3)' }}>
                  <p className="text-[10px] uppercase tracking-wide text-white/50">Platform Revenue</p>
                  <p className="text-2xl md:text-3xl mt-1 font-bold" style={{ fontFamily: 'Georgia, serif', color: '#c9a962' }} data-testid="hero-revenue">${(stats.total_revenue || 0).toLocaleString()}</p>
                  <p className="text-[11px] text-white/50 mt-0.5">promos & fees, all time</p>
                </div>
                <div className="rounded-xl p-4" style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}>
                  <p className="text-[10px] uppercase tracking-wide text-white/50">Marketplace Sales</p>
                  <p className="text-2xl md:text-3xl mt-1 font-bold" style={{ fontFamily: 'Georgia, serif' }}>${(stats.total_sales || 0).toLocaleString()}</p>
                  <p className="text-[11px] text-white/50 mt-0.5">{stats.total_orders || 0} orders all time</p>
                </div>
                <div className="rounded-xl p-4" style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}>
                  <p className="text-[10px] uppercase tracking-wide text-white/50">Members</p>
                  <p className="text-2xl md:text-3xl mt-1 font-bold" style={{ fontFamily: 'Georgia, serif' }}>{stats.total_users || 0}</p>
                  <p className="text-[11px] mt-0.5" style={{ color: '#9ec8a8' }}>
                    {analyticsData?.projections?.users_30d ? `→ ${analyticsData.projections.users_30d} projected in 30d` : `+${stats.new_users_today || 0} today`}
                  </p>
                </div>
                <div className="rounded-xl p-4" style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}>
                  <p className="text-[10px] uppercase tracking-wide text-white/50">Live Listings</p>
                  <p className="text-2xl md:text-3xl mt-1 font-bold" style={{ fontFamily: 'Georgia, serif' }}>{stats.active_listings || 0}</p>
                  <p className="text-[11px] mt-0.5" style={{ color: '#9ec8a8' }}>
                    {analyticsData?.projections?.listings_30d ? `→ ${analyticsData.projections.listings_30d} projected in 30d` : `${stats.sold_items || 0} sold`}
                  </p>
                </div>
              </div>
              {(queriesSummary.escalated > 0) && (
                <button onClick={() => handleTabChange('queries')} className="mt-4 text-xs px-3 py-1.5 rounded-full font-medium" style={{ backgroundColor: '#c9a962', color: '#0f2419' }} data-testid="hero-queries-alert">
                  🛟 {queriesSummary.escalated} customer quer{queriesSummary.escalated === 1 ? 'y' : 'ies'} need{queriesSummary.escalated === 1 ? 's' : ''} your attention →
                </button>
              )}
            </div>

            {/* Action Required panel */}
            <ActionRequired />

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                title="Total Users"
                value={stats.total_users || 0}
                subtitle={`+${stats.new_users_today || 0} today`}
                icon={Users}
                color="blue"
              />
              <StatCard
                title="Active Listings"
                value={stats.active_listings || 0}
                subtitle={`${stats.sold_items || 0} sold`}
                icon={Package}
                color="green"
              />
              <StatCard
                title="Total Sales"
                value={`$${(stats.total_sales || 0).toLocaleString()}`}
                subtitle="All time"
                icon={DollarSign}
                color="purple"
              />
              <StatCard
                title="Platform Revenue"
                value={`$${(stats.total_revenue || 0).toLocaleString()}`}
                subtitle="Promos & Fees"
                icon={TrendingUp}
                color="orange"
              />
            </div>

            {/* Orders Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                title="Total Orders"
                value={stats.total_orders || 0}
                icon={ShoppingCart}
                color="gray"
              />
              <StatCard
                title="Pending"
                value={stats.pending_orders || 0}
                icon={AlertCircle}
                color="yellow"
              />
              <StatCard
                title="To Ship"
                value={stats.items_to_ship || 0}
                icon={Truck}
                color="red"
              />
              <StatCard
                title="Delivered"
                value={stats.delivered_orders || 0}
                icon={Package}
                color="green"
              />
            </div>

            {/* Business Insights & Projections */}
            {analyticsData && (
              <div className="bg-gradient-to-br from-minted-50 to-teal-50 rounded-xl p-6 shadow-sm border border-minted-100">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-minted-600" />
                  Business Insights & 30-Day Projections
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Users Now → 30d</p>
                    <p className="text-2xl font-bold text-minted-600">
                      {stats.total_users || 0} → {analyticsData.projections?.users_30d || 0}
                    </p>
                    <p className="text-xs text-green-600">+{(analyticsData.projections?.users_30d || 0) - (stats.total_users || 0)} expected</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Listings Now → 30d</p>
                    <p className="text-2xl font-bold text-teal-600">
                      {stats.active_listings || 0} → {analyticsData.projections?.listings_30d || 0}
                    </p>
                    <p className="text-xs text-green-600">+{(analyticsData.projections?.listings_30d || 0) - (stats.active_listings || 0)} expected</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Active Today</p>
                    <p className="text-2xl font-bold text-blue-600">{analyticsData.user_activity?.active_today || 0}</p>
                    <p className="text-xs text-gray-400">{analyticsData.user_activity?.active_this_week || 0} this week</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Need Re-engagement</p>
                    <p className="text-2xl font-bold text-orange-600">{analyticsData.user_activity?.inactive_users || 0}</p>
                    <p className="text-xs text-gray-400">No activity 7d+</p>
                  </div>
                </div>
                
                {/* Engagement Metrics */}
                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Total Page Views</p>
                    <p className="text-xl font-bold text-gray-900">{analyticsData.page_views?.total || 0}</p>
                    <p className="text-xs text-green-600">+{analyticsData.page_views?.today || 0} today</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Product Views</p>
                    <p className="text-xl font-bold text-gray-900">{analyticsData.product_views?.total || 0}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Messages Sent</p>
                    <p className="text-xl font-bold text-gray-900">{analyticsData.messages?.total || 0}</p>
                    <p className="text-xs text-gray-400">{analyticsData.messages?.conversations || 0} conversations</p>
                  </div>
                </div>

                {/* Top Categories */}
                {analyticsData.categories?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Top Categories</p>
                    <div className="flex flex-wrap gap-2">
                      {analyticsData.categories.slice(0, 5).map((cat, i) => (
                        <span key={i} className="bg-white px-3 py-1 rounded-full text-sm font-medium text-gray-700 shadow-sm">
                          {cat.category}: {cat.count}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Recent Activity */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Recent Users */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-minted-600" />
                  Recent Users
                </h3>
                <div className="space-y-3">
                  {(dashboardData?.recent_users || []).slice(0, 5).map((user) => (
                    <div key={user.id} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-minted-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-minted-600">
                            {user.name?.charAt(0) || 'U'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-sm text-gray-900">{user.name}</p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                        </div>
                      </div>
                      <span className="text-xs text-gray-400">
                        {user.joined_date ? new Date(user.joined_date).toLocaleDateString() : '-'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Products */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5 text-minted-600" />
                  Recent Listings
                </h3>
                <div className="space-y-3">
                  {(dashboardData?.recent_products || []).slice(0, 5).map((product) => (
                    <div key={product.id} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div className="flex items-center gap-3">
                        <img
                          src={product.images?.[0] || 'https://via.placeholder.com/40'}
                          alt={product.title}
                          className="w-10 h-10 rounded-lg object-cover"
                        />
                        <div>
                          <p className="font-medium text-sm text-gray-900 line-clamp-1">{product.title}</p>
                          <p className="text-xs text-minted-600">${product.price}</p>
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${product.sold ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-600'}`}>
                        {product.sold ? 'Sold' : 'Active'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Actions Tab */}
        {activeTab === 'actions' && (
          <div className="space-y-6">
            {/* Founding Ten Tracker */}
            <FoundingTenTracker />

            {/* Pending Promotions */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                Pending Promotions ({pendingPromotions.length})
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                These promotions were paid for but not automatically activated. Click to activate manually.
              </p>
              {pendingPromotions.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Zap className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No pending promotions</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingPromotions.map((promo) => (
                    <div key={promo.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                      <div className="flex items-center gap-3">
                        {promo.product_image && (
                          <img 
                            src={promo.product_image} 
                            alt={promo.product_title}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        )}
                        <div>
                          <p className="font-medium text-gray-900">
                            {promo.promotion_type === 'boost' ? '🚀 Boost' : 
                             promo.promotion_type === 'feature' ? '⭐ Feature' : 
                             promo.promotion_type === 'urgent' ? '⚡ Urgent' :
                             promo.promotion_type === 'bump' ? '📈 Bump' : promo.promotion_type}
                          </p>
                          <p className="text-sm font-medium text-gray-700">
                            {promo.product_title || 'Unknown Product'}
                          </p>
                          <p className="text-xs text-gray-400">
                            ID: {promo.product_id?.slice(0, 8)}... • Paid: {new Date(promo.created_at).toLocaleDateString()} • ${promo.price}
                          </p>
                          {!promo.product_found && (
                            <span className="text-xs text-red-500 font-medium">⚠️ Product not found in database</span>
                          )}
                        </div>
                      </div>
                      <Button
                        onClick={() => activatePromotion(promo.id)}
                        disabled={actionLoading[promo.id] || !promo.product_found}
                        className={promo.product_found ? "bg-green-600 hover:bg-green-700" : "bg-gray-400 cursor-not-allowed"}
                      >
                        {actionLoading[promo.id] ? 'Activating...' : 'Activate'}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Email Actions */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Bell className="w-5 h-5 text-minted-600" />
                  Push Notifications
                </h3>
                <PushSubscriberBadge />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Broadcast to All Users</h4>
                  <p className="text-sm text-gray-500 mb-3">
                    Send a push notification to all subscribed app users.
                  </p>
                  <div className="space-y-2 mb-3">
                    <Input
                      placeholder="Notification title"
                      value={pushTitle}
                      onChange={(e) => setPushTitle(e.target.value)}
                      data-testid="push-title-input"
                    />
                    <Input
                      placeholder="Notification message"
                      value={pushMessage}
                      onChange={(e) => setPushMessage(e.target.value)}
                      data-testid="push-message-input"
                    />
                  </div>
                  <Button
                    onClick={async () => {
                      if (!pushMessage.trim()) { toast.error('Message is required'); return; }
                      setActionLoading(prev => ({ ...prev, push: true }));
                      try {
                        const res = await fetch(`${BACKEND_URL}/api/admin/push-broadcast`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ title: pushTitle || 'Minted Update', message: pushMessage })
                        });
                        const data = await res.json();
                        if (data.success) {
                          toast.success('Push notification sent!');
                          setPushTitle(''); setPushMessage('');
                        } else {
                          toast.error(data.error || 'No subscribed users yet - push will work once users opt in on the app');
                        }
                      } catch (err) { toast.error('Push send failed'); }
                      finally { setActionLoading(prev => ({ ...prev, push: false })); }
                    }}
                    disabled={actionLoading.push || !pushMessage.trim()}
                    className="w-full bg-minted-600 hover:bg-minted-700"
                    data-testid="send-push-btn"
                  >
                    {actionLoading.push ? 'Sending...' : 'Send Push Notification'}
                  </Button>
                </div>
                <div className="p-4 border rounded-lg bg-green-50 border-green-200">
                  <h4 className="font-medium text-gray-900 mb-2">Auto Push Triggers</h4>
                  <p className="text-sm text-gray-500 mb-2">
                    Push notifications are automatically sent for:
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-green-500"></span>New messages</li>
                    <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-green-500"></span>New offers on listings</li>
                    <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-green-500"></span>Price drop alerts</li>
                    <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-green-500"></span>Auction requests</li>
                    <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-green-500"></span>Outbid notifications</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Email Campaigns */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-minted-600" />
                Email Campaigns
              </h3>
              <div className="mb-4 max-w-md">
                <label className="text-xs font-medium text-gray-600 mb-1 block">Send test emails to</label>
                <Input
                  type="email"
                  value={testInbox}
                  onChange={(e) => setTestInbox(e.target.value)}
                  placeholder="your personal email, e.g. you@gmail.com"
                  data-testid="test-inbox-input"
                />
                <p className="text-[11px] text-gray-400 mt-1">Use an inbox you can actually open — admin@mintedbuysell.com.au may not receive mail.</p>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 border-2 rounded-lg border-minted-200 bg-minted-50/40 md:col-span-2" data-testid="compose-email-card">
                  <h4 className="font-medium text-gray-900 mb-1">✉️ Email a Customer</h4>
                  <p className="text-sm text-gray-500 mb-3">
                    Send a one-off branded email from <span className="font-medium">hello@mintedbuysell.com.au</span> — perfect for replying to customers without using Gmail. Replies come back to your inbox below.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">To</label>
                      <Input
                        type="email"
                        list="compose-user-emails"
                        value={compose.to}
                        onChange={(e) => setCompose(prev => ({ ...prev, to: e.target.value }))}
                        placeholder="customer@email.com"
                        data-testid="compose-to-input"
                      />
                      <datalist id="compose-user-emails">
                        {users.map(u => <option key={u.id} value={u.email}>{u.name}</option>)}
                      </datalist>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">Subject</label>
                      <Input
                        value={compose.subject}
                        onChange={(e) => setCompose(prev => ({ ...prev, subject: e.target.value }))}
                        placeholder="Re: your question about Minted"
                        data-testid="compose-subject-input"
                      />
                    </div>
                  </div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Message</label>
                  <Textarea
                    value={compose.message}
                    onChange={(e) => setCompose(prev => ({ ...prev, message: e.target.value }))}
                    placeholder={"Hi,\n\nThanks for reaching out..."}
                    rows={5}
                    className="mb-1"
                    data-testid="compose-message-input"
                  />
                  <p className="text-[11px] text-gray-400 mb-3">The customer sees it wrapped in the Minted template with a personal greeting — just write the message itself.</p>
                  <Button
                    onClick={async () => {
                      if (!compose.to.trim() || !compose.subject.trim() || !compose.message.trim()) {
                        toast.error('Fill in recipient, subject and message first');
                        return;
                      }
                      setComposeSending(true);
                      try {
                        const res = await fetch(`${BACKEND_URL}/api/admin/email/direct`, {
                          method: 'POST', headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ ...compose, reply_to: testInbox.trim() })
                        });
                        const data = await res.json();
                        if (data.sent) {
                          toast.success(`Email sent to ${data.to}${data.known_user ? '' : ' (not a registered user)'}`);
                          setCompose({ to: '', subject: '', message: '' });
                        } else {
                          toast.error(res.ok ? 'Send failed — check email logs' : (data.detail || 'Send failed'));
                        }
                      } catch { toast.error('Send failed'); }
                      finally { setComposeSending(false); }
                    }}
                    disabled={composeSending}
                    className="bg-minted-600 hover:bg-minted-700"
                    data-testid="compose-send-btn"
                  >
                    {composeSending ? 'Sending...' : 'Send Email'}
                  </Button>
                </div>
                <div className="p-4 border-2 rounded-lg md:col-span-2" style={{ borderColor: '#c9a962', backgroundColor: '#14100e' }} data-testid="founding-ten-card">
                  <h4 className="font-medium mb-2" style={{ color: '#f0e9df' }}>👑 The Founding Ten — Launch Campaign</h4>
                  <p className="text-sm mb-3" style={{ color: '#a89a8c' }}>
                    "Only 10 will ever get this" — the challenge email (20 listings + 5 referrals, 60 days, 10 lifetime-perk slots). Test it in your inbox, then release it to everyone.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={async () => {
                        setActionLoading(prev => ({ ...prev, ftTest: true }));
                        try {
                          const res = await fetch(`${BACKEND_URL}/api/admin/announcements/founding-ten`, {
                            method: 'POST', headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ test: true, test_email: testInbox.trim(), test_name: adminUser?.name || 'Admin' })
                          });
                          const data = await res.json();
                          if (data.sent) toast.success(`Test sent to ${data.to} — check your inbox`);
                          else toast.error('Test send failed — check email logs');
                        } catch { toast.error('Test send failed'); }
                        finally { setActionLoading(prev => ({ ...prev, ftTest: false })); }
                      }}
                      disabled={actionLoading.ftTest}
                      variant="outline" className="flex-1 min-w-[160px] bg-transparent border-[#c9a962] text-[#c9a962] hover:bg-[#c9a962]/15 hover:text-[#e8d9b5]"
                      data-testid="founding-ten-test-btn"
                    >
                      {actionLoading.ftTest ? 'Sending...' : 'Send Test to My Inbox'}
                    </Button>
                    <Button
                      onClick={async () => {
                        if (!window.confirm('Launch The Founding Ten to ALL users? Make sure the test email looked perfect first.')) return;
                        setActionLoading(prev => ({ ...prev, ftBulk: true }));
                        try {
                          const res = await fetch(`${BACKEND_URL}/api/admin/announcements/founding-ten`, {
                            method: 'POST', headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ test: false })
                          });
                          const data = await res.json();
                          toast.success(`👑 The Founding Ten is launching to ${data.recipients} users — you'll be notified when it finishes.`);
                        } catch { toast.error('Could not start the campaign'); }
                        finally { setActionLoading(prev => ({ ...prev, ftBulk: false })); }
                      }}
                      disabled={actionLoading.ftBulk}
                      className="flex-1 min-w-[160px]" style={{ backgroundColor: '#c9a962', color: '#14100e' }}
                      data-testid="founding-ten-bulk-btn"
                    >
                      {actionLoading.ftBulk ? 'Launching...' : '👑 Launch to Everyone'}
                    </Button>
                  </div>
                </div>
                <div className="p-4 border-2 rounded-lg border-amber-300 bg-amber-50 md:col-span-2" data-testid="platform-update-card">
                  <h4 className="font-medium text-gray-900 mb-2">📣 Platform Update Announcement</h4>
                  <p className="text-sm text-gray-500 mb-3">
                    "What's new at Minted & what's coming" — Private Collection, boosts, services, notifications, App Store launch & global shipping. Send a test to your inbox first, then release it to everyone.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={async () => {
                        setActionLoading(prev => ({ ...prev, updateTest: true }));
                        try {
                          const res = await fetch(`${BACKEND_URL}/api/admin/announcements/platform-update`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ test: true, test_email: testInbox.trim(), test_name: adminUser?.name || 'Admin' })
                          });
                          const data = await res.json();
                          if (data.sent) toast.success(`Test sent to ${data.to} — check your inbox`);
                          else toast.error('Test send failed — check email logs');
                        } catch { toast.error('Test send failed'); }
                        finally { setActionLoading(prev => ({ ...prev, updateTest: false })); }
                      }}
                      disabled={actionLoading.updateTest}
                      variant="outline"
                      className="flex-1 min-w-[160px]"
                      data-testid="platform-update-test-btn"
                    >
                      {actionLoading.updateTest ? 'Sending...' : 'Send Test to My Inbox'}
                    </Button>
                    <Button
                      onClick={async () => {
                        if (!window.confirm('Send the platform update announcement to ALL users? Make sure you checked the test email first.')) return;
                        setActionLoading(prev => ({ ...prev, updateBulk: true }));
                        try {
                          const res = await fetch(`${BACKEND_URL}/api/admin/announcements/platform-update`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ test: false })
                          });
                          const data = await res.json();
                          toast.success(`Announcement going out to ${data.recipients} users in the background — you'll get a notification when it finishes.`);
                        } catch { toast.error('Could not start the campaign'); }
                        finally { setActionLoading(prev => ({ ...prev, updateBulk: false })); }
                      }}
                      disabled={actionLoading.updateBulk}
                      className="flex-1 min-w-[160px] bg-minted-800 hover:bg-minted-700"
                      data-testid="platform-update-bulk-btn"
                    >
                      {actionLoading.updateBulk ? 'Starting...' : 'Send to Everyone'}
                    </Button>
                  </div>
                </div>
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">📧 Weekly Newsletter</h4>
                  <p className="text-sm text-gray-500 mb-3">
                    Send AI-generated newsletter featuring new listings and marketplace highlights.
                  </p>
                  <Button
                    onClick={sendWeeklyNewsletter}
                    disabled={sendingEmails}
                    className="w-full bg-minted-600 hover:bg-minted-700"
                  >
                    {sendingEmails ? 'Sending...' : 'Send Newsletter'}
                  </Button>
                </div>

                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">👋 Re-engagement</h4>
                  <p className="text-sm text-gray-500 mb-3">
                    Send reminder emails to users who have not been active in the last 30 days.
                  </p>
                  <Button
                    onClick={sendReengagementEmails}
                    disabled={sendingEmails}
                    variant="outline"
                    className="w-full"
                  >
                    {sendingEmails ? 'Sending...' : 'Send Re-engagement'}
                  </Button>
                </div>

                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">📢 Promotional Email</h4>
                  <p className="text-sm text-gray-500 mb-3">
                    Send promotional email to all users encouraging them to list items.
                  </p>
                  <Button
                    onClick={sendPromoEmail}
                    disabled={actionLoading.promo}
                    variant="outline"
                    className="w-full"
                  >
                    {actionLoading.promo ? 'Sending...' : 'Send Promo Email'}
                  </Button>
                </div>

                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">🔔 Push Awareness Email</h4>
                  <p className="text-sm text-gray-500 mb-3">
                    Email all users asking them to turn on push notifications.
                  </p>
                  <Button
                    onClick={async () => {
                      if (!window.confirm('Email ALL users asking them to enable notifications?')) return;
                      try {
                        const res = await fetch(`${BACKEND_URL}/api/admin/send-push-awareness`, { method: 'POST' });
                        const data = await res.json();
                        toast.success(`Campaign started — emailing ${data.recipients} users in the background. You'll get a notification when it finishes.`);
                      } catch {
                        toast.error('Could not start campaign');
                      }
                    }}
                    variant="outline"
                    className="w-full"
                    data-testid="push-awareness-email-btn"
                  >
                    Send Push Awareness Email
                  </Button>
                </div>

                <div className="p-4 border rounded-lg bg-gray-50">
                  <h4 className="font-medium text-gray-900 mb-2">⏰ Auto Newsletter</h4>
                  <p className="text-sm text-gray-500 mb-3">
                    Note: Automated weekly newsletters require a cron job on the server.
                  </p>
                  <p className="text-xs text-amber-600">
                    Currently manual only - infrastructure setup required for automation.
                  </p>
                </div>

                <div className="p-4 border rounded-lg border-minted-200 bg-minted-50">
                  <h4 className="font-medium text-gray-900 mb-2">Test Email</h4>
                  <p className="text-sm text-gray-500 mb-3">
                    Send a test welcome email to verify Resend integration is working.
                  </p>
                  <div className="flex gap-2">
                    <Input
                      placeholder="test@email.com"
                      value={testEmailAddr}
                      onChange={(e) => setTestEmailAddr(e.target.value)}
                      className="flex-1"
                      data-testid="test-email-input"
                    />
                    <Button
                      onClick={sendTestEmail}
                      disabled={actionLoading.testEmail || !testEmailAddr}
                      className="bg-minted-600 hover:bg-minted-700"
                      data-testid="send-test-email-btn"
                    >
                      {actionLoading.testEmail ? 'Sending...' : 'Send'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Credit Wallet */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Credit User Wallet</h4>
                  <p className="text-sm text-gray-500 mb-3">
                    Manually add funds to a user's available wallet balance.
                  </p>
                  <div className="flex gap-2">
                    <Input
                      placeholder="user@email.com"
                      value={creditEmail}
                      onChange={(e) => setCreditEmail(e.target.value)}
                      className="flex-1"
                      data-testid="credit-email-input"
                    />
                    <Input
                      placeholder="$0.00"
                      type="number"
                      value={creditAmount}
                      onChange={(e) => setCreditAmount(e.target.value)}
                      className="w-28"
                      data-testid="credit-amount-input"
                    />
                    <Button
                      onClick={async () => {
                        if (!creditEmail || !creditAmount) return;
                        setActionLoading(prev => ({ ...prev, creditWallet: true }));
                        try {
                          const res = await fetch(`${BACKEND_URL}/api/admin/wallet/credit`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ email: creditEmail, amount: parseFloat(creditAmount), reason: 'Admin manual credit' })
                          });
                          const data = await res.json();
                          if (data.success) {
                            toast.success(`$${parseFloat(creditAmount).toFixed(2)} credited to ${data.user_name}. New balance: $${data.new_balance.toFixed(2)}`);
                            setCreditEmail('');
                            setCreditAmount('');
                          } else {
                            toast.error(data.detail || 'Failed');
                          }
                        } catch { toast.error('Failed to credit wallet'); }
                        setActionLoading(prev => ({ ...prev, creditWallet: false }));
                      }}
                      disabled={actionLoading.creditWallet || !creditEmail || !creditAmount}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="credit-wallet-btn"
                    >
                      {actionLoading.creditWallet ? 'Adding...' : 'Credit'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Set/Adjust Wallet Balance */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Set Wallet Balance</h4>
                  <p className="text-sm text-gray-500 mb-3">
                    Override a user's wallet balance to a specific amount (for corrections).
                  </p>
                  <div className="space-y-2">
                    <Input
                      placeholder="user@email.com"
                      value={adjustEmail}
                      onChange={(e) => setAdjustEmail(e.target.value)}
                      data-testid="adjust-email-input"
                    />
                    <div className="flex gap-2">
                      <Input
                        placeholder="Available balance"
                        type="number"
                        value={adjustAvailable}
                        onChange={(e) => setAdjustAvailable(e.target.value)}
                        className="flex-1"
                        data-testid="adjust-available-input"
                      />
                      <Input
                        placeholder="Pending balance"
                        type="number"
                        value={adjustPending}
                        onChange={(e) => setAdjustPending(e.target.value)}
                        className="flex-1"
                        data-testid="adjust-pending-input"
                      />
                    </div>
                    <Input
                      placeholder="Reason (e.g. 'Correcting double-credit')"
                      value={adjustReason}
                      onChange={(e) => setAdjustReason(e.target.value)}
                      data-testid="adjust-reason-input"
                    />
                    <Button
                      onClick={async () => {
                        if (!adjustEmail || (adjustAvailable === '' && adjustPending === '')) return;
                        if (!window.confirm(`Set wallet for ${adjustEmail}?\nAvailable: ${adjustAvailable || 'unchanged'}\nPending: ${adjustPending || 'unchanged'}`)) return;
                        setActionLoading(prev => ({ ...prev, adjustWallet: true }));
                        try {
                          const body = { email: adjustEmail, reason: adjustReason || 'Admin balance correction' };
                          if (adjustAvailable !== '') body.available_balance = parseFloat(adjustAvailable);
                          if (adjustPending !== '') body.pending_balance = parseFloat(adjustPending);
                          const res = await fetch(`${BACKEND_URL}/api/admin/wallet/set`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(body)
                          });
                          const data = await res.json();
                          if (data.success) {
                            toast.success(`Wallet updated for ${data.user_name}. Available: $${data.new_wallet.available_balance}, Pending: $${data.new_wallet.pending_balance}`);
                            setAdjustEmail(''); setAdjustAvailable(''); setAdjustPending(''); setAdjustReason('');
                          } else {
                            toast.error(data.detail || 'Failed');
                          }
                        } catch { toast.error('Failed to adjust wallet'); }
                        setActionLoading(prev => ({ ...prev, adjustWallet: false }));
                      }}
                      disabled={actionLoading.adjustWallet || !adjustEmail || (adjustAvailable === '' && adjustPending === '')}
                      className="bg-amber-600 hover:bg-amber-700 w-full"
                      data-testid="adjust-wallet-btn"
                    >
                      {actionLoading.adjustWallet ? 'Updating...' : 'Set Balance'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Email Logs */}
              {emailLogs.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-500" />
                    Recent Email Activity ({emailLogs.length})
                  </h4>
                  <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                    {emailLogs.slice(0, 20).map((log, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 text-sm">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${log.success ? 'bg-green-500' : 'bg-red-500'}`} />
                          <div className="min-w-0">
                            <p className="text-gray-900 truncate">{log.recipient}</p>
                            <p className="text-xs text-gray-500 truncate">{log.type} — {log.subject}</p>
                          </div>
                        </div>
                        <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
                          {log.sent_at ? new Date(log.sent_at).toLocaleString() : '-'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Store Management */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Store className="w-5 h-5 text-purple-600" />
                Quick Actions
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <Button
                  onClick={() => { handleTabChange('users'); }}
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2"
                >
                  <Users className="w-6 h-6" />
                  <span>Manage Users</span>
                </Button>
                <Button
                  onClick={() => { handleTabChange('products'); }}
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2"
                >
                  <Package className="w-6 h-6" />
                  <span>Manage Products</span>
                </Button>
                <Button
                  onClick={() => { handleTabChange('orders'); }}
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2"
                >
                  <ShoppingCart className="w-6 h-6" />
                  <span>Manage Orders</span>
                </Button>
              </div>
            </div>

            {/* System Tools */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                🔧 System Tools
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">🔓 Release Pending Funds</h4>
                  <p className="text-sm text-gray-500 mb-3">
                    Release funds for orders past the 7-day hold period.
                  </p>
                  <Button
                    onClick={releasePendingFunds}
                    disabled={actionLoading.releaseFunds}
                    variant="outline"
                    className="w-full"
                  >
                    {actionLoading.releaseFunds ? 'Releasing...' : 'Release Due Funds'}
                  </Button>
                </div>

                <div className="p-4 border rounded-lg bg-emerald-50 border-emerald-200">
                  <h4 className="font-medium text-gray-900 mb-2">S3 Image Storage</h4>
                  <p className="text-sm text-gray-500 mb-3">
                    Migrate all product images to AWS S3 for blazing fast load times.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={async () => {
                        setActionLoading(prev => ({ ...prev, migrateS3: true }));
                        try {
                          const res = await fetch(`${BACKEND_URL}/api/admin/migrate-images-s3`, { method: 'POST' });
                          const data = await res.json();
                          if (data.success) toast.success(data.message);
                          else toast.error(data.detail || 'Failed');
                        } catch { toast.error('Migration failed'); }
                        setActionLoading(prev => ({ ...prev, migrateS3: false }));
                      }}
                      disabled={actionLoading.migrateS3}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      data-testid="migrate-s3-btn"
                    >
                      {actionLoading.migrateS3 ? 'Migrating...' : 'Migrate All to S3'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={async () => {
                        setActionLoading(prev => ({ ...prev, genThumbs: true }));
                        try {
                          const res = await fetch(`${BACKEND_URL}/api/admin/generate-thumbnails`, { method: 'POST' });
                          const data = await res.json();
                          if (data.success) toast.success(data.message);
                        } catch {}
                        setActionLoading(prev => ({ ...prev, genThumbs: false }));
                      }}
                      disabled={actionLoading.genThumbs}
                    >
                      {actionLoading.genThumbs ? '...' : 'Thumbnails'}
                    </Button>
                    <Button
                      variant="outline"
                      className="text-amber-700 border-amber-300 hover:bg-amber-50"
                      onClick={async () => {
                        if (!window.confirm('This will clear all cached thumbnails and regenerate them at higher quality. Continue?')) return;
                        setActionLoading(prev => ({ ...prev, regenThumbs: true }));
                        try {
                          const res = await fetch(`${BACKEND_URL}/api/admin/regenerate-thumbnails`, { method: 'POST' });
                          const data = await res.json();
                          if (data.success) toast.success(`Cleared ${data.cleared} old thumbnails, regenerating ${data.queued} at higher quality`);
                        } catch { toast.error('Failed to regenerate'); }
                        setActionLoading(prev => ({ ...prev, regenThumbs: false }));
                      }}
                      disabled={actionLoading.regenThumbs}
                      data-testid="regen-thumbs-btn"
                    >
                      {actionLoading.regenThumbs ? '...' : 'HD Regen'}
                    </Button>
                    <Button
                      variant="outline"
                      className="text-blue-700 border-blue-300 hover:bg-blue-50"
                      onClick={async () => {
                        setActionLoading(prev => ({ ...prev, imgDiag: true }));
                        try {
                          const res = await fetch(`${BACKEND_URL}/api/admin/image-diagnostic`);
                          const data = await res.json();
                          const msg = `Total: ${data.total} | S3: ${data.has_s3_url} | B64: ${data.has_b64_prefixed} | Raw B64: ${data.has_b64_raw} | Empty: ${data.has_empty_images} | Cached thumbs: ${data.has_thumbnail_cached} | Issues: ${data.products_needing_fix?.length || 0}`;
                          toast.info(msg, { duration: 15000 });
                          console.log('Image Diagnostic:', JSON.stringify(data, null, 2));
                        } catch { toast.error('Diagnostic failed'); }
                        setActionLoading(prev => ({ ...prev, imgDiag: false }));
                      }}
                      disabled={actionLoading.imgDiag}
                      data-testid="img-diagnostic-btn"
                    >
                      {actionLoading.imgDiag ? '...' : 'Diagnose'}
                    </Button>
                    <Button
                      variant="outline"
                      className="text-red-700 border-red-300 hover:bg-red-50"
                      onClick={async () => {
                        if (!window.confirm('Fix 12 products pointing to wrong S3 bucket? This will copy images to the correct bucket.')) return;
                        setActionLoading(prev => ({ ...prev, fixBucket: true }));
                        try {
                          const res = await fetch(`${BACKEND_URL}/api/admin/fix-wrong-bucket`, { method: 'POST' });
                          const data = await res.json();
                          if (data.success) {
                            toast.success(`Fixed ${data.fixed} products (${data.failed} failed)`);
                          } else {
                            toast.error(data.error || 'Fix failed');
                          }
                        } catch { toast.error('Fix request failed'); }
                        setActionLoading(prev => ({ ...prev, fixBucket: false }));
                      }}
                      disabled={actionLoading.fixBucket}
                      data-testid="fix-bucket-btn"
                    >
                      {actionLoading.fixBucket ? 'Fixing...' : 'Fix Images'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Support Tab - Product Search & Recovery */}
        {/* Product lookup (merged into Listings) */}
        {activeTab === 'products' && (
          <div className="space-y-6 mt-6">
            {/* Product Search */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Search className="w-5 h-5 text-minted-600" />
                Find Lost Products
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Search for products by user email, title, or category. Useful for helping users find their listings.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <Label className="text-sm font-medium">User Email</Label>
                  <Input
                    placeholder="e.g., user@example.com"
                    value={productSearch.email}
                    onChange={(e) => setProductSearch({...productSearch, email: e.target.value})}
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Product Title</Label>
                  <Input
                    placeholder="e.g., iPhone, Dress"
                    value={productSearch.title}
                    onChange={(e) => setProductSearch({...productSearch, title: e.target.value})}
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Category</Label>
                  <Input
                    placeholder="e.g., Health, Beauty, Electronics"
                    value={productSearch.category}
                    onChange={(e) => setProductSearch({...productSearch, category: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={async () => {
                    setSearchLoading(true);
                    try {
                      const params = new URLSearchParams();
                      if (productSearch.email) params.append('email', productSearch.email);
                      if (productSearch.title) params.append('title', productSearch.title);
                      if (productSearch.category) params.append('category', productSearch.category);
                      
                      const response = await fetch(`${BACKEND_URL}/api/admin/search-products?${params}`);
                      const data = await response.json();
                      setSearchResults(data);
                      toast.success(`Found ${data.count} products`);
                    } catch (error) {
                      toast.error('Search failed');
                    } finally {
                      setSearchLoading(false);
                    }
                  }}
                  disabled={searchLoading || (!productSearch.email && !productSearch.title && !productSearch.category)}
                  className="bg-minted-600 hover:bg-minted-700"
                >
                  {searchLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                  Search Products
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setProductSearch({ email: '', title: '', category: '' });
                    setSearchResults(null);
                  }}
                >
                  Clear
                </Button>
              </div>
              
              {/* Search Results */}
              {searchResults && (
                <div className="mt-6">
                  <h4 className="font-medium text-gray-900 mb-3">
                    Search Results ({searchResults.count} products)
                  </h4>
                  {searchResults.count === 0 ? (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-yellow-800">No products found matching your search criteria.</p>
                      <p className="text-sm text-yellow-600 mt-1">
                        This could mean the product was never saved, or the user may be using a different account.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {searchResults.products.map((product) => (
                        <div key={product.id} className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{product.title}</p>
                            <p className="text-sm text-gray-500">
                              Category: {product.category} | Price: ${product.price}
                            </p>
                            <p className="text-xs text-gray-400">
                              Seller: {product.seller_email || product.seller_id}
                            </p>
                            <p className="text-xs text-gray-400">
                              ID: {product.id} | Sold: {product.sold ? 'Yes' : 'No'}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(`/product/${product.id}`, '_blank')}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Product Audit Trail */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-minted-600" />
                Recent Product Activity
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Recent product creation, updates, and deletions for debugging.
              </p>
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const response = await fetch(`${BACKEND_URL}/api/admin/product-audit`);
                    const data = await response.json();
                    if (data.length === 0) {
                      toast.info('No audit records found');
                    } else {
                      toast.success(`Found ${data.length} recent activities`);
                      // Could display in a modal or expand this section
                    }
                  } catch (error) {
                    toast.error('Failed to load audit trail');
                  }
                }}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Load Recent Activity
              </Button>
            </div>
          </div>
        )}

        {/* Finance Tab */}
        {activeTab === 'finance' && (
          <div className="space-y-6">
            {financeLoading ? (
              <div className="bg-white rounded-xl p-8 text-center">
                <RefreshCw className="w-8 h-8 text-gray-400 mx-auto mb-2 animate-spin" />
                <p className="text-gray-500">Loading finance data...</p>
              </div>
            ) : (
              <>
                {/* Period Selector and Actions */}
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex gap-2">
                    {['today', 'week', 'month', 'year', 'all'].map((period) => (
                      <button
                        key={period}
                        onClick={() => {
                          setFinancePeriod(period);
                          fetchFinanceData(period);
                        }}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          financePeriod === period
                            ? 'bg-minted-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {period.charAt(0).toUpperCase() + period.slice(1)}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={releasePendingFunds}
                      disabled={actionLoading.releaseFunds}
                      variant="outline"
                      size="sm"
                    >
                      {actionLoading.releaseFunds ? 'Releasing...' : '🔓 Release Due Funds'}
                    </Button>
                    <Button
                      onClick={() => exportFinanceData('all')}
                      variant="outline"
                      size="sm"
                    >
                      📥 Export Data
                    </Button>
                  </div>
                </div>

                {/* Financial Summary Cards */}
                {financeData && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-green-500">
                      <p className="text-xs text-gray-500 uppercase">Platform Revenue</p>
                      <p className="text-2xl font-bold text-gray-900">${financeData.revenue?.total?.toFixed(2)}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Promos: ${financeData.revenue?.promotions?.toFixed(2)} | Fees: ${financeData.revenue?.payout_fees?.toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-blue-500">
                      <p className="text-xs text-gray-500 uppercase">Total GMV</p>
                      <p className="text-2xl font-bold text-gray-900">${financeData.sales?.total?.toFixed(2)}</p>
                      <p className="text-xs text-gray-400 mt-1">{financeData.sales?.order_count} orders</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-purple-500">
                      <p className="text-xs text-gray-500 uppercase">Payouts</p>
                      <p className="text-2xl font-bold text-gray-900">${financeData.payouts?.completed?.toFixed(2)}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Pending: ${financeData.payouts?.pending?.toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-orange-500">
                      <p className="text-xs text-gray-500 uppercase">Held Funds</p>
                      <p className="text-2xl font-bold text-gray-900">${financeData.sales?.held_funds?.toFixed(2)}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Disputed: ${financeData.sales?.disputed_funds?.toFixed(2)}
                      </p>
                    </div>
                  </div>
                )}

                {/* Card Processing Fee Setting */}
                <CardFeeSettings />

                {/* Pending Payouts */}
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    💸 Pending Payouts ({payouts.filter(p => p.status === 'pending').length})
                  </h3>
                  {payouts.filter(p => p.status === 'pending').length === 0 ? (
                    <div className="text-center py-6 text-gray-400">
                      <DollarSign className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No pending payouts</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">User</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Amount</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Bank</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Requested</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {payouts.filter(p => p.status === 'pending').map((payout) => (
                            <tr key={payout.id} className="hover:bg-gray-50">
                              <td className="px-3 py-3">
                                <p className="font-medium">{payout.user_name}</p>
                                <p className="text-xs text-gray-400">{payout.user_email}</p>
                              </td>
                              <td className="px-3 py-3">
                                <p className="font-bold text-green-600">${payout.amount?.toFixed(2)}</p>
                                <p className="text-xs text-gray-400">Fee: ${payout.fee?.toFixed(2)}</p>
                              </td>
                              <td className="px-3 py-3">
                                {payout.payout_method === 'paypal' || payout.paypal_email ? (
                                  <div>
                                    <p className="text-blue-600 font-medium text-xs">PayPal</p>
                                    <p className="text-sm text-gray-900">{payout.paypal_email || payout.full_paypal_email}</p>
                                  </div>
                                ) : (
                                  <div>
                                    <p className="font-medium text-gray-900 text-sm">{payout.bank_name || payout.full_bank_name}</p>
                                    <p className="text-xs text-gray-600">BSB: {payout.full_bsb || payout.bsb}</p>
                                    <p className="text-xs text-gray-600">Acc: {payout.full_account_number || payout.account_number_full || ('****' + payout.account_number_last4)}</p>
                                    <p className="text-xs text-gray-500">Name: {payout.full_account_name || payout.account_name}</p>
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-3 text-gray-500">
                                {new Date(payout.created_at).toLocaleDateString()}
                              </td>
                              <td className="px-3 py-3">
                                <div className="flex gap-1">
                                  <Button
                                    onClick={() => processPayout(payout.id, 'complete')}
                                    disabled={actionLoading[payout.id]}
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700 text-xs"
                                  >
                                    ✓ Complete
                                  </Button>
                                  <Button
                                    onClick={() => processPayout(payout.id, 'fail')}
                                    disabled={actionLoading[payout.id]}
                                    size="sm"
                                    variant="destructive"
                                    className="text-xs"
                                  >
                                    ✗ Fail
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Open Disputes */}
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    ⚠️ Open Disputes ({disputes.filter(d => d.status === 'open').length})
                  </h3>
                  {disputes.filter(d => d.status === 'open').length === 0 ? (
                    <div className="text-center py-6 text-gray-400">
                      <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No open disputes</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {disputes.filter(d => d.status === 'open').map((dispute) => (
                        <div key={dispute.id} className="border rounded-lg p-4 bg-red-50">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-gray-900">
                                {dispute.reason?.replace('_', ' ').toUpperCase()}
                              </p>
                              <p className="text-sm text-gray-600 mt-1">{dispute.description}</p>
                              <p className="text-xs text-gray-400 mt-2">
                                Product: {dispute.product_title} | Amount: ${dispute.order_amount?.toFixed(2)}
                              </p>
                              <p className="text-xs text-gray-400">
                                Reporter: {dispute.reporter_name} ({dispute.reporter_email})
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={() => resolveDispute(dispute.id, 'buyer')}
                                disabled={actionLoading[dispute.id]}
                                size="sm"
                                variant="outline"
                              >
                                Refund Buyer
                              </Button>
                              <Button
                                onClick={() => resolveDispute(dispute.id, 'seller')}
                                disabled={actionLoading[dispute.id]}
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                              >
                                Release to Seller
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Payout History */}
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-4">📋 Payout History</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">User</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Amount</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Bank</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {payouts.slice(0, 20).map((payout) => (
                          <tr key={payout.id} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-gray-500">
                              {new Date(payout.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-3 py-2">
                              <p className="font-medium">{payout.user_name}</p>
                            </td>
                            <td className="px-3 py-2 font-medium">${payout.amount?.toFixed(2)}</td>
                            <td className="px-3 py-2 text-gray-500">
                              {payout.bank_name} ****{payout.account_number_last4}
                            </td>
                            <td className="px-3 py-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                payout.status === 'completed' ? 'bg-green-100 text-green-700' :
                                payout.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                payout.status === 'failed' ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {payout.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Analytics Tab */}
        {/* Growth charts (merged into Dashboard) */}
        {activeTab === 'overview' && analyticsData && (
          <div className="space-y-6 mt-6">
            {!analyticsData ? (
              <div className="bg-white rounded-xl p-8 text-center">
                <RefreshCw className="w-8 h-8 text-gray-400 mx-auto mb-2 animate-spin" />
                <p className="text-gray-500">Loading analytics...</p>
              </div>
            ) : (
              <>
                {/* Charts Row 1: Growth + Sales */}
                <div className="grid md:grid-cols-2 gap-6">
                  <GrowthChart data={analyticsData.growth} title="User & Listing Growth (30 Days)" />
                  <SalesChart data={analyticsData.growth} />
                </div>

                {/* Charts Row 2: Daily Activity */}
                <DailyActivityChart data={analyticsData.growth} />

                {/* Charts Row 3: Category + User Engagement + Top Products */}
                <div className="grid md:grid-cols-3 gap-6">
                  <CategoryChart categories={analyticsData.categories} />
                  <UserActivityChart userActivity={analyticsData.user_activity} />
                  <TopProductsChart products={analyticsData.product_views?.top_products} />
                </div>

                {/* Email Campaigns */}
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-4">Email Campaigns</h3>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={sendWeeklyNewsletter}
                      disabled={sendingEmails}
                      className="bg-minted-600 hover:bg-minted-700"
                    >
                      {sendingEmails ? 'Sending...' : 'Send Weekly Newsletter'}
                    </Button>
                    <Button
                      onClick={sendReengagementEmails}
                      disabled={sendingEmails}
                      variant="outline"
                    >
                      {sendingEmails ? 'Sending...' : 'Send Re-engagement Emails'}
                    </Button>
                  </div>
                  {analyticsData.email_campaigns?.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm text-gray-500 mb-2">Recent campaigns:</p>
                      <div className="space-y-2">
                        {analyticsData.email_campaigns.slice(0, 3).map((c, i) => (
                          <div key={i} className="text-sm bg-gray-50 p-2 rounded flex justify-between">
                            <span className="font-medium">{c.type}</span>
                            <span className="text-gray-500">
                              {c.sent_count} sent {c.sent_at && `• ${new Date(c.sent_at).toLocaleDateString()}`}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Categories & Locations Tables */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-4">Top Categories</h3>
                    <div className="space-y-2">
                      {(analyticsData.categories || []).map((cat, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-gray-700">{cat.category || 'Unknown'}</span>
                          <span className="font-medium text-gray-900">{cat.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-4">Users by Location</h3>
                    <div className="space-y-2">
                      {(analyticsData.locations || []).map((loc, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-gray-700">{loc.location || 'Unknown'}</span>
                          <span className="font-medium text-gray-900">{loc.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">All Users ({users.length})</h3>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
            </div>
            <div className="divide-y">
              {users
                .filter(u => 
                  u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  u.email?.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map((user) => (
                <div key={user.id} className="flex items-center justify-between p-4 hover:bg-gray-50" data-testid={`user-row-${user.id}`}>
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-10 h-10 bg-minted-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-minted-600">
                        {user.name?.charAt(0) || 'U'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900">{user.name}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                    <div className="text-sm text-gray-500 hidden md:flex md:items-center md:gap-4">
                      <span>{user.location || '-'}</span>
                      <span className="text-xs bg-minted-50 text-minted-700 px-2 py-0.5 rounded-full">{user.listings_count || 0} listings</span>
                      <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{user.orders_count || 0} orders</span>
                      <span className="text-xs text-gray-400">{user.joined_date ? new Date(user.joined_date).toLocaleDateString() : '-'}</span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => renameUser(user)}
                    className="ml-4"
                    data-testid={`rename-user-${user.id}`}
                  >
                    Rename
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteUser(user.id)}
                    className="ml-2 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                    data-testid={`delete-user-${user.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">All Products ({products.length})</h3>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
            </div>
            <div className="divide-y">
              {products
                .filter(p => 
                  p.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  p.category?.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map((product) => (
                <div key={product.id} className="flex items-center justify-between p-4 hover:bg-gray-50">
                  <div className="flex items-center gap-4 flex-1">
                    <img
                      src={product.images?.[0] || 'https://via.placeholder.com/40'}
                      alt={product.title}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{product.title}</p>
                      <p className="text-sm text-gray-500">{product.category} • ${product.price}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      product.sold 
                        ? 'bg-gray-100 text-gray-600' 
                        : product.is_example 
                          ? 'bg-amber-100 text-amber-600'
                          : 'bg-green-100 text-green-600'
                    }`}>
                      {product.sold ? 'Sold' : product.is_example ? 'Example' : 'Active'}
                    </span>
                  </div>
                  <div className="flex gap-2 ml-4">
                    {product.sold && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          try {
                            await fetch(`${BACKEND_URL}/api/admin/products/${product.id}/unmark-sold`, { method: 'POST' });
                            setProducts(prev => prev.map(p => p.id === product.id ? {...p, sold: false} : p));
                            if (refreshProducts) refreshProducts();
                            toast.success(`"${product.title}" marked as available`);
                          } catch (err) {
                            toast.error('Failed to unmark sold');
                          }
                        }}
                        className="text-stone-700 border-stone-300 hover:bg-stone-100"
                        data-testid={`unmark-sold-${product.id}`}
                      >
                        Relist
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deleteProduct(product.id)}
                      className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-6">
            {/* Payment Transactions */}
            <div className="bg-white rounded-xl shadow-sm">
              <div className="p-4 border-b flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Payment Transactions ({paymentTransactions.length})</h3>
                <Button
                  onClick={handleReconcile}
                  disabled={reconciling}
                  size="sm"
                  className="bg-minted-600 hover:bg-minted-700"
                  data-testid="reconcile-btn"
                >
                  {reconciling ? 'Checking...' : 'Reconcile Payments'}
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Session</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {paymentTransactions.map((txn) => (
                      <tr key={txn.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-xs font-mono text-gray-600">
                          {txn.session_id?.slice(0, 20)}...
                          <span className={`ml-1 text-[10px] px-1 rounded ${txn.session_id?.startsWith('cs_live') ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'}`}>
                            {txn.session_id?.startsWith('cs_live') ? 'LIVE' : 'TEST'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {typeof txn.metadata === 'object' ? txn.metadata?.product_title : 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium">${txn.amount}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            txn.payment_status === 'paid' ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'
                          }`}>
                            {txn.payment_status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {txn.created_at ? new Date(txn.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-4 py-3">
                          {txn.payment_status === 'pending' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={async () => {
                                try {
                                  const res = await fetch(`${BACKEND_URL}/api/admin/create-order-from-transaction`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ transaction_id: txn.id })
                                  });
                                  const data = await res.json();
                                  if (data.success) {
                                    toast.success(`Order created! $${data.amount}`);
                                    fetchOrders();
                                  } else {
                                    toast.info(data.message || 'Could not create order');
                                  }
                                } catch { toast.error('Failed'); }
                              }}
                              data-testid="create-order-btn"
                            >
                              Create Order
                            </Button>
                          )}
                          {txn.payment_status === 'paid' && (
                            <span className="text-xs text-green-600">Processed</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {paymentTransactions.length === 0 && (
                  <div className="text-center py-8 text-gray-500 text-sm">No payment transactions</div>
                )}
              </div>
            </div>

            {/* Orders Table */}
            <div className="bg-white rounded-xl shadow-sm">
              <div className="p-4 border-b flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">All Orders ({orders.length})</h3>
              </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Buyer / Seller</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shipping</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {orders.map((order) => {
                    const si = order.shipping_info || {};
                    const hasShippingAddr = si.address || si.city || si.postcode;
                    return (
                    <tr key={order.id} className="hover:bg-gray-50" data-testid={`admin-order-row-${order.id}`}>
                      <td className="px-4 py-3">
                        <div className="text-xs font-mono text-gray-600">#{order.id?.slice(0, 8)}</div>
                        {order.payment_intent_id && <div className="text-[10px] text-gray-400 mt-0.5">Pay: {order.payment_intent_id?.slice(0, 12)}...</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {order.product_image && <img src={order.product_image} alt="" className="w-8 h-8 rounded object-cover" />}
                          <span className="text-sm text-gray-900">{order.product_title || order.product_id?.slice(0, 12)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div className="text-xs">B: {order.buyer_name || order.buyer_id?.slice(0, 8)}</div>
                        {order.buyer_email && <div className="text-[10px] text-gray-400">{order.buyer_email}</div>}
                        <div className="text-xs mt-1">S: {order.seller_name || order.seller_id?.slice(0, 8)}</div>
                        {order.seller_email && <div className="text-[10px] text-gray-400">{order.seller_email}</div>}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">
                        <div className="font-medium">{order.shipping_method || 'N/A'}</div>
                        {hasShippingAddr ? (
                          <div className="text-[10px] text-gray-400 mt-0.5 max-w-[150px]">
                            {si.full_name && <div>{si.full_name}</div>}
                            {si.address && <div>{si.address}</div>}
                            <div>{[si.city, si.state, si.postcode].filter(Boolean).join(' ')}</div>
                          </div>
                        ) : (
                          <div className="text-[10px] text-gray-300 mt-0.5">No address</div>
                        )}
                        {order.tracking_number && <div className="text-[10px] text-blue-500 mt-0.5">Track: {order.tracking_number}</div>}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">${parseFloat(order.total_price || 0).toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          order.status === 'completed' ? 'bg-green-100 text-green-600' :
                          order.status === 'shipped' ? 'bg-blue-100 text-blue-600' :
                          order.status === 'pending' ? 'bg-yellow-100 text-yellow-600' :
                          order.status === 'cancelled' ? 'bg-red-100 text-red-600' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {order.status}
                        </span>
                        {/* Funds status badge */}
                        <div className="mt-1">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                            order.funds_status === 'released' ? 'bg-green-50 text-green-600' :
                            order.funds_status === 'held' ? 'bg-amber-50 text-amber-600' :
                            order.funds_status === 'refunded' ? 'bg-red-50 text-red-500' :
                            order.funds_status === 'disputed' ? 'bg-orange-50 text-orange-600' :
                            'bg-gray-50 text-gray-400'
                          }`}>
                            Funds: {order.funds_status || 'held'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {order.created_at ? new Date(order.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <div className="flex gap-1">
                          <select
                            value={order.status}
                            onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                            className="text-xs border rounded px-1 py-1 w-24"
                          >
                            <option value="pending">Pending</option>
                            <option value="shipped">Shipped</option>
                            <option value="delivered">Delivered</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                          {order.status !== 'cancelled' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs text-red-600 border-red-200 hover:bg-red-50"
                              onClick={async () => {
                                if (!window.confirm(`Cancel order and refund $${order.total_price}?`)) return;
                                try {
                                  const res = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}/cancel`, { method: 'POST' });
                                  const data = await res.json();
                                  if (data.success) {
                                    toast.success(data.message);
                                    fetchOrders();
                                    if (refreshProducts) refreshProducts();
                                  } else toast.error('Cancel failed');
                                } catch { toast.error('Cancel failed'); }
                              }}
                              data-testid={`cancel-order-${order.id}`}
                            >
                              Cancel & Refund
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-xs text-gray-400"
                            onClick={async () => {
                              if (!window.confirm('Delete this order permanently?')) return;
                              try {
                                const res = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}`, { method: 'DELETE' });
                                const data = await res.json();
                                if (data.success) {
                                  toast.success('Order deleted');
                                  fetchOrders();
                                  if (refreshProducts) refreshProducts();
                                }
                              } catch { toast.error('Delete failed'); }
                            }}
                          >
                            Delete
                          </Button>
                          </div>
                          {/* Release Funds Button */}
                          {(order.funds_status === 'held' || (!order.funds_status && order.status !== 'cancelled' && order.status !== 'refunded')) && order.status !== 'cancelled' && (
                            <Button
                              size="sm"
                              className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white w-full"
                              disabled={actionLoading[`release_${order.id}`]}
                              onClick={async () => {
                                if (!window.confirm(`Release $${parseFloat(order.total_price || 0).toFixed(2)} to seller?`)) return;
                                setActionLoading(prev => ({ ...prev, [`release_${order.id}`]: true }));
                                try {
                                  const res = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}/release-funds`, { method: 'POST' });
                                  const data = await res.json();
                                  if (data.success) {
                                    toast.success(`Released $${data.amount.toFixed(2)} to seller`);
                                    fetchOrders();
                                  } else {
                                    toast.error(data.detail || 'Failed to release funds');
                                  }
                                } catch { toast.error('Failed to release funds'); }
                                setActionLoading(prev => ({ ...prev, [`release_${order.id}`]: false }));
                              }}
                              data-testid={`release-funds-${order.id}`}
                            >
                              {actionLoading[`release_${order.id}`] ? 'Releasing...' : 'Release Funds'}
                            </Button>
                          )}
                          {order.funds_status === 'released' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-[10px] border-amber-300 text-amber-700 hover:bg-amber-50 w-full"
                              disabled={actionLoading[`rerelease_${order.id}`]}
                              onClick={async () => {
                                if (!window.confirm(`Force re-credit $${parseFloat(order.total_price || 0).toFixed(2)} to seller wallet?\n\nUse this if the wallet wasn't credited properly.`)) return;
                                setActionLoading(prev => ({ ...prev, [`rerelease_${order.id}`]: true }));
                                try {
                                  const res = await fetch(`${BACKEND_URL}/api/admin/orders/${order.id}/release-funds?force=true`, { method: 'POST' });
                                  const data = await res.json();
                                  if (data.success) {
                                    toast.success(`Force-credited $${data.amount.toFixed(2)} to seller wallet`);
                                    fetchOrders();
                                  } else {
                                    toast.error(data.detail || 'Failed');
                                  }
                                } catch { toast.error('Failed to force re-credit'); }
                                setActionLoading(prev => ({ ...prev, [`rerelease_${order.id}`]: false }));
                              }}
                              data-testid={`force-release-${order.id}`}
                            >
                              {actionLoading[`rerelease_${order.id}`] ? 'Crediting...' : 'Force Re-Credit Wallet'}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              {orders.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No orders yet
                </div>
              )}
            </div>
          </div>
          </div>
        )}

        {/* Payouts Tab */}
        {/* Itemised fees ledger (part of Finance) */}
        {activeTab === 'finance' && <div className="mt-6"><RevenueLedger /></div>}
        {activeTab === 'finance' && <div className="mt-6"><ShippingMarginReport adminUser={adminUser} /></div>}

        {/* Payout requests (merged into Finance) */}
        {activeTab === 'finance' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-semibold text-gray-900 text-lg mb-4 flex items-center gap-2">
                <Banknote className="w-5 h-5 text-minted-600" /> Payout Requests
              </h3>
              <p className="text-sm text-gray-500 mb-4">When a seller requests a payout, their bank details appear here. Transfer via Stripe, then mark complete.</p>
              
              {payoutRequests.length === 0 ? (
                <div className="text-center py-12 text-gray-400">No payout requests yet</div>
              ) : (
                <div className="space-y-4">
                  {payoutRequests.map((req) => (
                    <div key={req.id} className={`border rounded-lg p-4 ${
                      req.status === 'pending' ? 'border-amber-200 bg-amber-50' :
                      req.status === 'completed' ? 'border-green-200 bg-green-50' :
                      'border-gray-200 bg-gray-50'
                    }`} data-testid={`payout-request-${req.id}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-semibold text-gray-900">{req.user_name}</p>
                          <p className="text-xs text-gray-500">{req.user_email}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{new Date(req.created_at).toLocaleString()}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-gray-900">${req.amount?.toFixed(2)}</p>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                            req.status === 'pending' ? 'bg-amber-200 text-amber-800' :
                            req.status === 'completed' ? 'bg-green-200 text-green-800' :
                            'bg-red-200 text-red-800'
                          }`}>
                            {req.status?.toUpperCase()}
                          </span>
                        </div>
                      </div>

                      {/* Bank Details */}
                      {req.bank_details && req.bank_details.length > 0 && (
                        <div className="bg-white rounded-md p-3 border border-gray-200 mb-3">
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                            <Building2 className="w-3 h-3" /> Bank Details for Stripe Transfer
                          </p>
                          {req.bank_details.map((bank, idx) => (
                            <div key={idx} className="text-sm space-y-1">
                              {bank.paypal_email ? (
                                <p><span className="text-gray-500">PayPal:</span> <span className="font-mono font-medium">{bank.paypal_email}</span></p>
                              ) : (
                                <>
                                  <p><span className="text-gray-500">Bank:</span> <span className="font-medium">{bank.bank_name}</span></p>
                                  <p><span className="text-gray-500">Account Name:</span> <span className="font-medium">{bank.account_name}</span></p>
                                  <p><span className="text-gray-500">BSB:</span> <span className="font-mono font-medium">{bank.bsb}</span></p>
                                  <p><span className="text-gray-500">Account:</span> <span className="font-mono font-medium">{bank.account_number}</span></p>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {req.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white flex-1"
                            disabled={actionLoading[`payout_${req.id}`]}
                            onClick={async () => {
                              if (!window.confirm(`Mark $${req.amount?.toFixed(2)} payout to ${req.user_name} as completed?`)) return;
                              setActionLoading(prev => ({ ...prev, [`payout_${req.id}`]: true }));
                              try {
                                const res = await fetch(`${BACKEND_URL}/api/admin/payout-requests/${req.id}/complete`, { method: 'PUT' });
                                const data = await res.json();
                                if (data.success) {
                                  toast.success('Payout marked as completed');
                                  fetchPayoutRequests();
                                } else toast.error(data.detail || 'Failed');
                              } catch { toast.error('Error processing payout'); }
                              setActionLoading(prev => ({ ...prev, [`payout_${req.id}`]: false }));
                            }}
                            data-testid={`complete-payout-${req.id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" /> Mark Paid
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-200 text-red-600 hover:bg-red-50"
                            disabled={actionLoading[`payout_${req.id}`]}
                            onClick={async () => {
                              if (!window.confirm(`Reject payout request? $${req.amount?.toFixed(2)} will be returned to seller's wallet.`)) return;
                              setActionLoading(prev => ({ ...prev, [`payout_${req.id}`]: true }));
                              try {
                                const res = await fetch(`${BACKEND_URL}/api/admin/payout-requests/${req.id}/reject`, { method: 'PUT' });
                                const data = await res.json();
                                if (data.success) {
                                  toast.success('Payout rejected, funds returned');
                                  fetchPayoutRequests();
                                } else toast.error(data.detail || 'Failed');
                              } catch { toast.error('Error rejecting payout'); }
                              setActionLoading(prev => ({ ...prev, [`payout_${req.id}`]: false }));
                            }}
                            data-testid={`reject-payout-${req.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}


        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 text-lg flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-minted-600" /> Service Listings ({adminServices.length})
                </h3>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="bg-amber-600 hover:bg-amber-700 text-xs"
                    disabled={actionLoading.notifyPaymentDue}
                    onClick={async () => {
                      setActionLoading(prev => ({ ...prev, notifyPaymentDue: true }));
                      try {
                        const res = await fetch(`${BACKEND_URL}/api/admin/services/notify-payment-due`, { method: 'POST' });
                        const data = await res.json();
                        if (data.success) {
                          toast.success(data.notified > 0
                            ? `Notified ${data.notified} service provider${data.notified === 1 ? '' : 's'} (in-app + push + email)`
                            : 'No services currently have payment due');
                        } else {
                          toast.error('Failed to send notifications');
                        }
                      } catch { toast.error('Failed'); }
                      setActionLoading(prev => ({ ...prev, notifyPaymentDue: false }));
                    }}
                    data-testid="notify-payment-due-btn"
                  >
                    {actionLoading.notifyPaymentDue ? 'Sending...' : 'Notify Payment Due'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={fetchAdminServices}
                    data-testid="refresh-services-btn"
                  >
                    Refresh
                  </Button>
                </div>
              </div>

              {adminServices.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No service listings</p>
              ) : (
                <div className="space-y-3">
                  {adminServices.map((svc) => {
                    const freeUntil = svc.free_until ? new Date(svc.free_until) : null;
                    const isFreeActive = freeUntil && freeUntil > new Date();
                    const subStatus = svc.subscription_status || 'trialing';
                    const isExpired = svc.status === 'expired';

                    return (
                      <div key={svc.id} className="border rounded-lg p-4 flex items-start justify-between gap-3" data-testid={`admin-service-${svc.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-medium text-gray-900 text-sm">{svc.title}</h4>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                              isExpired ? 'bg-red-50 text-red-600' :
                              subStatus === 'active' ? 'bg-green-50 text-green-600' :
                              isFreeActive ? 'bg-blue-50 text-blue-600' :
                              'bg-amber-50 text-amber-600'
                            }`}>
                              {isExpired ? 'Expired' : subStatus === 'active' ? 'Subscribed' : isFreeActive ? 'Free Trial' : 'Payment Due'}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">{svc.provider_name} · {svc.category} · {svc.location || 'No location'}</p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            Views: {svc.views || 0} · Created: {svc.created_at ? new Date(svc.created_at).toLocaleDateString() : '-'}
                            {freeUntil && ` · Free until: ${freeUntil.toLocaleDateString()}`}
                          </p>
                          {svc.contact_email && <p className="text-xs text-gray-400">Contact: {svc.contact_email}</p>}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs text-red-600 border-red-200 hover:bg-red-50 shrink-0"
                          disabled={actionLoading[`del_svc_${svc.id}`]}
                          onClick={async () => {
                            if (!window.confirm(`Delete service "${svc.title}"? This cannot be undone.`)) return;
                            setActionLoading(prev => ({ ...prev, [`del_svc_${svc.id}`]: true }));
                            try {
                              const res = await fetch(`${BACKEND_URL}/api/services/${svc.id}`, { method: 'DELETE' });
                              const data = await res.json();
                              if (data.success) {
                                toast.success(`Service "${svc.title}" deleted`);
                                fetchAdminServices();
                              } else {
                                toast.error('Failed to delete service');
                              }
                            } catch { toast.error('Failed to delete service'); }
                            setActionLoading(prev => ({ ...prev, [`del_svc_${svc.id}`]: false }));
                          }}
                          data-testid={`delete-service-${svc.id}`}
                        >
                          {actionLoading[`del_svc_${svc.id}`] ? 'Deleting...' : 'Delete'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, color }) => {
  const colors = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600',
    gray: 'bg-gray-100 text-gray-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    red: 'bg-red-100 text-red-600',
  };

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500">{title}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
    </div>
  );
};

export default AdminDashboard;
